import React from 'react';
import { Switch, Route, RouteComponentProps } from 'react-router-dom';
import routes from '../../routes';
import {
    AppRouteComponentMatchParams,
    OrgRouteComponentMatchParams,
    ReleaseRouteComponentMatchParams,
} from '../../typescriptHelpers';
import Sider from 'antd/lib/layout/Sider';
import AppSettingsNavigationContent from './AppSettingsNavigationContent';
import { AppNavigationContent } from './AppNavigationContent';
import {
    ValidatedAndLoadedReleaseWrapper,
    ValidatedAppRouteWrapper,
    ValidatedOrgRouteWrapper,
} from '../ValidatedRouteComponents';
import { ReleaseStepsSidebar } from '@/components/sidebar/ReleaseStepsSidebar';
import { NavigationToolbar } from './NavigationToolbar';
import { NavigationHeader } from './NavigationHeader';
import { NavigationFooter } from './NavigationFooter';
import { useNavigationContext } from '../../utils/hooks/useNavigationContext';
import { useSidebarColors, useSidebarTheme } from '../styles/themeMode';
import { OrgNavigationContent } from './OrgOverview/OrgNavigationContent';
import { useOrgNavigationFilters } from './OrgOverview/OrgNavigationFiltersContext';

export interface NavigationSidebarProps {
    orgId: string | null;
    appId: string | null;
}

export const NavigationSidebar: React.FC<NavigationSidebarProps> = ({ orgId, appId }) => {
    const { releaseId, releaseIdParam, isOrgRoute, isOrgSettingsRoute } = useNavigationContext();
    const sidebarTheme = useSidebarTheme();
    const sc = useSidebarColors();
    const { filters: orgNavigationFilters, setFilters: setOrgNavigationFilters } = useOrgNavigationFilters();

    return (
        <Sider
            width="280px"
            style={{
                backgroundColor: sc.bg,
                minWidth: '280px',
            }}
        >
            <div
                className="hidden-scrollbar"
                data-sidebar-theme={sidebarTheme}
                style={{
                    height: '100%',
                    backgroundColor: sc.bg,
                    overflowY: 'auto',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            paddingBottom: 20,
                        }}
                    >
                        {orgId && (
                            <NavigationHeader
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                releaseIdParam={releaseIdParam}
                                isOrgRoute={isOrgRoute}
                                isOrgSettingsRoute={isOrgSettingsRoute}
                            />
                        )}

                        <NavigationToolbar />

                        <Switch>
                            <Route
                                path={routes.release}
                                render={(routeProps: RouteComponentProps) => {
                                    const releaseRouteProps =
                                        routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                    return (
                                        <ValidatedAndLoadedReleaseWrapper
                                            {...releaseRouteProps}
                                            showLoader={true}
                                            showNotFoundComponent
                                            showError
                                            withTracking
                                        >
                                            {(validatedReleaseRouteProps) => {
                                                return <ReleaseStepsSidebar {...validatedReleaseRouteProps} />;
                                            }}
                                        </ValidatedAndLoadedReleaseWrapper>
                                    );
                                }}
                            />
                            <Route
                                path={routes.appSettings}
                                render={(routeProps: RouteComponentProps) => {
                                    const appRouteProps =
                                        routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                                    return (
                                        <ValidatedAppRouteWrapper {...appRouteProps}>
                                            {(validatedAppProps) => (
                                                <AppSettingsNavigationContent
                                                    appId={validatedAppProps.appId}
                                                    orgId={validatedAppProps.orgId}
                                                />
                                            )}
                                        </ValidatedAppRouteWrapper>
                                    );
                                }}
                            />
                            <Route
                                path={routes.app}
                                render={(routeProps: RouteComponentProps) => {
                                    const appRouteProps =
                                        routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                                    return (
                                        <ValidatedAppRouteWrapper {...appRouteProps}>
                                            {(validatedAppProps) => (
                                                <AppNavigationContent
                                                    appId={validatedAppProps.appId}
                                                    orgId={validatedAppProps.orgId}
                                                />
                                            )}
                                        </ValidatedAppRouteWrapper>
                                    );
                                }}
                            />
                            <Route
                                path={routes.org}
                                render={(routeProps: RouteComponentProps) => {
                                    const orgRouteProps =
                                        routeProps as RouteComponentProps<OrgRouteComponentMatchParams>;
                                    return (
                                        <ValidatedOrgRouteWrapper {...orgRouteProps}>
                                            {(validatedOrgComponentProps) => (
                                                <OrgNavigationContent
                                                    orgId={validatedOrgComponentProps.orgId}
                                                    isOrgSettingsRoute={isOrgSettingsRoute}
                                                    filters={orgNavigationFilters}
                                                    setFilters={setOrgNavigationFilters}
                                                />
                                            )}
                                        </ValidatedOrgRouteWrapper>
                                    );
                                }}
                            />
                        </Switch>
                    </div>
                    <NavigationFooter orgId={orgId} />
                </div>
            </div>
        </Sider>
    );
};
